(() => {
  const CHAT_SELECTOR = 'shopify-chat, #shopify-chat';
  const OFFSET_VAR = '--shopify-agent-activator-offset';
  const DEFAULT_GAP = 16;
  const DEFAULT_OFFSET = 16;
  const DEFAULT_AVOID_SELECTORS = [
    '[data-chat-avoidance]',
    'cart-drawer[open] > .cart-drawer__summary',
    'cart-drawer[aria-hidden="false"] > .cart-drawer__summary',
    'quick-add-drawer[open] > .drawer__footer',
    'quick-add-drawer[aria-hidden="false"] > .drawer__footer',
    'sticky-atc-panel:not(.sticky-atc-panel--out):not([hidden])',
    '.sticky-atc-panel:not(.sticky-atc-panel--out):not([hidden])'
  ];

  let baseOffset = null;
  let lastAppliedOffset = null;
  let updateQueued = false;

  function getConfig() {
    return window.themeChatAvoidance || {};
  }

  function getSelectors() {
    const config = getConfig();
    const customSelectors = Array.isArray(config.selectors) ? config.selectors : [];
    return [...DEFAULT_AVOID_SELECTORS, ...customSelectors].filter(Boolean);
  }

  function getGap() {
    const gap = Number(getConfig().gap);
    return Number.isFinite(gap) ? gap : DEFAULT_GAP;
  }

  function getChatElement() {
    return document.querySelector(CHAT_SELECTOR);
  }

  function parsePixelValue(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getCurrentOffset(chat) {
    const inlineValue = chat.style.getPropertyValue(OFFSET_VAR);
    const computedValue = getComputedStyle(chat).getPropertyValue(OFFSET_VAR);
    return parsePixelValue(inlineValue || computedValue);
  }

  function updateBaseOffset(chat) {
    const currentOffset = getCurrentOffset(chat);

    if (baseOffset === null) {
      baseOffset = currentOffset || DEFAULT_OFFSET;
      return;
    }

    if (lastAppliedOffset === null || Math.abs(currentOffset - lastAppliedOffset) > 1) {
      baseOffset = Math.max(baseOffset, currentOffset || DEFAULT_OFFSET);
    }
  }

  function isElementVisible(element) {
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);

    return rect.width > 0
      && rect.height > 0
      && style.display !== 'none'
      && style.visibility !== 'hidden'
      && parseFloat(style.opacity || '1') > 0.01;
  }

  function getViewportHeight() {
    return window.visualViewport ? window.visualViewport.height : window.innerHeight;
  }

  function getBottomAvoidanceHeight(element) {
    if (!isElementVisible(element)) return 0;

    const hiddenDrawer = element.closest('[aria-hidden="true"]');
    if (hiddenDrawer) return 0;

    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    const viewportHeight = getViewportHeight();

    if (rect.bottom <= 0 || rect.top >= viewportHeight) return 0;

    const occupiedFromBottom = Math.max(0, viewportHeight - Math.max(rect.top, 0));
    const outerHeight = rect.height + parsePixelValue(style.marginTop) + parsePixelValue(style.marginBottom);
    return Math.max(outerHeight, occupiedFromBottom);
  }

  function getAvoidanceHeight() {
    const seen = new Set();

    return getSelectors().reduce((maxHeight, selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        maxHeight = Math.max(maxHeight, getBottomAvoidanceHeight(element));
      });

      return maxHeight;
    }, 0);
  }

  function applyOffset() {
    updateQueued = false;

    const chat = getChatElement();
    if (!chat) return;

    updateBaseOffset(chat);

    const avoidanceHeight = getAvoidanceHeight();
    const nextOffset = Math.ceil(Math.max(
      baseOffset || DEFAULT_OFFSET,
      avoidanceHeight > 0 ? avoidanceHeight + getGap() : 0
    ));

    if (lastAppliedOffset !== nextOffset) {
      chat.style.setProperty(OFFSET_VAR, `${nextOffset}px`);
      lastAppliedOffset = nextOffset;
    }
  }

  function scheduleUpdate() {
    if (updateQueued) return;

    updateQueued = true;
    window.requestAnimationFrame(applyOffset);
  }

  function bindEvents() {
    const updateEvents = [
      'DOMContentLoaded',
      'load',
      'resize',
      'scroll',
      'on:cart-drawer:before-open',
      'on:cart-drawer:after-open',
      'on:cart-drawer:before-close',
      'on:cart-drawer:after-close',
      'on:quickbuy:before-open',
      'on:quickbuy:after-open',
      'on:quickbuy:before-close',
      'on:quickbuy:after-close',
      'dispatch:cart-drawer:refresh',
      'on:css-var-height:update',
      'shopify:section:load'
    ];

    updateEvents.forEach((eventName) => {
      window.addEventListener(eventName, scheduleUpdate);
      document.addEventListener(eventName, scheduleUpdate);
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate);
      window.visualViewport.addEventListener('scroll', scheduleUpdate);
    }

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(scheduleUpdate);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style', 'open', 'aria-hidden', 'hidden'],
        childList: true,
        subtree: true
      });
    }

    scheduleUpdate();
  }

  bindEvents();
})();
